import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoginSelectors } from '../../redux/LoginRedux';
import LoginCreators from '../../redux/LoginRedux';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import './HomePage.css';

interface HomePageState {
  loading: boolean;
  isOnline: boolean;
  isStandalone: boolean;
}

interface HomePageProps {
  accessToken: string | null;
  isFetching: boolean;
  isSuccess: boolean;
  hasError: boolean;
  dispatch: any;
}

class HomePage extends Component<HomePageProps, HomePageState> {
  constructor(props: HomePageProps) {
    super(props);
    this.state = {
      loading: false,
      isOnline: navigator.onLine,
      isStandalone: window.matchMedia && window.matchMedia('(display-mode: standalone)').matches
    };
  }

  componentDidMount() {
    // Initialize page data
    console.log('HomePage mounted');
    console.log('Redux State:', {
      accessToken: this.props.accessToken,
      isFetching: this.props.isFetching,
      isSuccess: this.props.isSuccess,
      hasError: this.props.hasError
    });

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ isOnline: true });
  };

  handleOffline = () => {
    this.setState({ isOnline: false });
  };

  handleTestLogin = () => {
    // Test login with mock data
    this.props.dispatch(LoginCreators.loginRequest(
      'test@example.com',
      'password123',
      'mock_notification_token',
      'en'
    ));
  };

  render() {
    const { loading, isOnline, isStandalone } = this.state;
    const { accessToken, isFetching, isSuccess, hasError } = this.props;
    const isAuthenticated = !!accessToken;

    return (
      <div className="home-page">
        <div className="home-container">
          <div className="home-header">
            <h1 className="home-title">Welcome to CDCHK Web</h1>
            <p className="home-subtitle">
              React.js PWA version of the CDC Connects mobile app
            </p>
            <LanguageSwitcher />
          </div>
          
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="home-content">
              {/* Authentication Status */}
              <div className="auth-status-section">
                <h2>🔐 Authentication Status</h2>
                <div className="auth-status">
                  <div className="status-item">
                    <span className="status-label">Status:</span>
                    <span className={`status-value ${isAuthenticated ? 'authenticated' : 'not-authenticated'}`}>
                      {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
                    </span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Token:</span>
                    <span className="status-value">
                      {isAuthenticated ? '✅ Present' : '❌ Missing'}
                    </span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Network:</span>
                    <span className={`status-value ${isOnline ? 'online' : 'offline'}`}>
                      {isOnline ? '✅ Online' : '❌ Offline'}
                    </span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">PWA Mode:</span>
                    <span className="status-value">
                      {isStandalone ? '✅ Standalone' : '🌐 Browser'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="quick-actions-section">
                <h2>🚀 Quick Actions</h2>
                <div className="actions-grid">
                  {!isAuthenticated ? (
                    <>
                      <Link to="/login" className="action-card login-card">
                        <div className="action-icon">🔑</div>
                        <h3>Login</h3>
                        <p>Access your account</p>
                      </Link>
                      <Link to="/signup" className="action-card signup-card">
                        <div className="action-icon">📝</div>
                        <h3>Sign Up</h3>
                        <p>Create a new account</p>
                      </Link>
                      <Link to="/forgot-password" className="action-card forgot-password-card">
                        <div className="action-icon">🔒</div>
                        <h3>Forgot Password</h3>
                        <p>Reset your password</p>
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/dashboard" className="action-card dashboard-card">
                        <div className="action-icon">📊</div>
                        <h3>Dashboard</h3>
                        <p>View your main dashboard</p>
                      </Link>
                      <Link to="/calendar" className="action-card calendar-card">
                        <div className="action-icon">📅</div>
                        <h3>Calendar</h3>
                        <p>Manage appointments</p>
                      </Link>
                      <Link to="/goals" className="action-card goals-card">
                        <div className="action-icon">🎯</div>
                        <h3>Goals</h3>
                        <p>Track progress and goals</p>
                      </Link>
                      <Link to="/settings" className="action-card settings-card">
                        <div className="action-icon">⚙️</div>
                        <h3>Settings</h3>
                        <p>Manage your preferences</p>
                      </Link>
                    </>
                  )}
                </div>
              </div>

              {/* Development Tools (Only in development) */}
              {process.env.NODE_ENV === 'development' && (
                <div className="dev-tools-section">
                  <h2>🛠️ Development Tools</h2>
                  <div className="dev-tools">
                    <button 
                      className="dev-button test-login-btn"
                      onClick={this.handleTestLogin}
                      disabled={isFetching}
                    >
                      {isFetching ? 'Testing...' : 'Test Login'}
                    </button>
                    <div className="dev-status">
                      <p><strong>Redux State:</strong></p>
                      <ul>
                        <li>Fetching: {isFetching ? 'Yes' : 'No'}</li>
                        <li>Success: {isSuccess ? 'Yes' : 'No'}</li>
                        <li>Error: {hasError ? 'Yes' : 'No'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* System Status */}
              <div className="system-status-section">
                <h2>⚙️ System Status</h2>
                <div className="system-status">
                  <div className="status-item">
                    <span className="status-label">Redux Store:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Redux Persist:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">Redux Saga:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">API Proxy:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">PWA Features:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                  <div className="status-item">
                    <span className="status-label">i18n:</span>
                    <span className="status-value success">✅ Active</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="home-footer">
                <p>CDC Connects Web Application v1.0.0</p>
                <p>Built with React.js, TypeScript, and Redux</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  accessToken: state.login.accessToken,
  isFetching: state.login.fetching,
  isSuccess: state.login.success,
  hasError: state.login.error,
});

export default connect(mapStateToProps)(HomePage);
